import { action, observable, makeObservable } from "mobx";
import { api } from "../lib/api";
import { parseISO } from 'date-fns'
import { User } from "../models/user";
import { IApiLoginResponse } from "../models/apiResponses";
import {
  IApiLoginRefreshResponse,
  IUserResponse
} from "../models/apiResponses";
import {
  IOrganizationTheme, IDistrict, ISchool
} from '../models/apiDataStructures';
import { IApiLoginRefreshRequest, ILoginRequest } from "../models/apiRequests";
import { SharedStateStore } from "./SharedStateStore";
import { appHistory } from "../appHistory";
import e from "express";
import { result } from "lodash";

const ACCESS_TOKEN_KEY = "access_token";
const ORGANIZATION_THEME_KEY = "organization_theme";

export interface Jwt {
  exp: number;
}

export class UserStore extends SharedStateStore {
  user: User = new User({});
  theme: IOrganizationTheme | undefined;
  initialPage: string = "";
  isAuthenticated = false;
  // Indicates if the user store tried to load user details from the remote side
  storeLoaded = false;

  adminModalOpen = false;
  overrideSchool: ISchool | undefined = undefined;
  overrideDistrict: IDistrict | undefined = undefined;

  // @ts-ignore
  refreshTokenTimeout: Timeout = null;

  constructor() {
    super()
    makeObservable(this, {
      user: observable,
      theme: observable,
      initialPage: observable,
      isAuthenticated: observable,
      storeLoaded: observable,
      overrideSchool: observable,
      overrideDistrict: observable,
      adminModalOpen: observable,
      openAdminModal: action,
      closeAdminModal: action,
      login: action,
      logout: action,
      refreshAccessToken: action,
      loadUser: action,
      setUser: action,
      saveUser: action,
      setAccessToken: action,
      setInitialPage: action,
      setOrganizationTheme: action,
      setOverrideOrganizations: action
    });

    if (!this.getStoredAccessToken()) {
      return;
    }

  }

  openAdminModal = () => {
    if (!this.user.isAppAdmin) {
      console.error("Access denied")
      return
    }
    this.setState({ adminModalOpen: true })
  }

  closeAdminModal = () => {
    this.setState({ adminModalOpen: false })
  }

  setOverrideOrganizations(school: ISchool | undefined, district: IDistrict | undefined) {
    if (!this.user.isAppAdmin) {
      console.error("Organization override disabled for non Application Admins")
      return
    }
    this.setState({
      overrideSchool: school,
      overrideDistrict: district
    })
    const noThemeResetRoutes = ['/myschool','mydistrict']
    if( !noThemeResetRoutes.some(function (v) { return window.location.hash.includes(v) })) {
      this.updateOrganizationTheme(true, "school")
    }
  }

  updateOrganizationTheme(forceUpdate=false, themeType="") {
    let storedTheme = this.getStoredOrganizationTheme();
    if (storedTheme && !forceUpdate) {
      const { refreshToken } = storedTheme
      const expirationDate = parseISO(refreshToken)
      const now = new Date();
      if (now > expirationDate) {
        this.requestOrganizationTheme(themeType);
      } else {
        this.setOrganizationTheme(storedTheme)
      }
    } else {
      this.requestOrganizationTheme(themeType);
    }
  }

  async requestOrganizationTheme(themeType): Promise<void> {
    try {
      let route = "district-theme?"
      if (themeType) route = route + `${themeType}=True&`
      if (this.overrideSchool) route = route + `school_id=${this.overrideSchool.id}&`
      if (this.overrideDistrict) route = route + `district_id=${this.overrideDistrict.id}&`
      let result = await api.find<IOrganizationTheme>(route)
      this.setOrganizationTheme(result)
    } catch (err) {
      throw err;
    }
  }

  setOrganizationTheme(theme: IOrganizationTheme) {
    this.setState({theme})
    this.storeOrganizationTheme(theme)
  }

  getStoredOrganizationTheme(): IOrganizationTheme | undefined {
    if (typeof window === "undefined") return;
    const value = window.localStorage.getItem(ORGANIZATION_THEME_KEY);
    return value ? JSON.parse(value) : undefined;
  }

  storeOrganizationTheme(theme: IOrganizationTheme): void {
    if (typeof window === "undefined") return;
    let themeString = JSON.stringify(theme)
    window.localStorage.setItem(ORGANIZATION_THEME_KEY, themeString);
  }

  async login({ username = "", password = "" }): Promise<IApiLoginResponse['user']> {
    try {
      let result = await api.post<IApiLoginResponse, ILoginRequest>("login", {
        username,
        password
      });
      this.setState({ isAuthenticated: true })
      this.setUser(result.user);
      this.setAccessToken(result.access_token);
      this.loadUser(); // fetch the rest of the users data, including roles
      return result.user
    } catch (err) {
      throw err;
    } 

  }

  async logout() {
    this.setState({
      isAuthenticated: false,
      overrideDistrict: null,
      overrideSchool: null
    })
    this.setAccessToken(null);
    this.setUser(null);
  }

  async refreshAccessToken(currentAccessToken: string) {
    try {
      let result = await api.post<
        IApiLoginRefreshResponse,
        IApiLoginRefreshRequest
      >("login/refresh", {
        token: currentAccessToken
      });

      if (
        result &&
        result.non_field_errors &&
        result.non_field_errors.length > 0
      ) {
        throw result.non_field_errors[0];
      }

      this.setUser(result.user);
      this.setAccessToken(result.access_token);
    } catch (err) {
      appHistory.push("/login");
    }
  }

  async loadUser() {
    try {
      const result = await api.find<IUserResponse>("user", {
        dontCatchBannedResponses: true
      });

      this.setUser(result);

      if (!this.isAuthenticated) {
        let accessToken = this.getStoredAccessToken();
        this.refreshTokenBeforeExpire(accessToken);
      }

      this.setState({ isAuthenticated: true })
      this.updateOrganizationTheme();

      return result;

    } catch (err) {
      throw err;
      // appHistory.push("/login");
    } 
  }

  async saveUser(user: any) {
    let newUser = await api.update(`user/${this.user.id}`, user);
    let userCopy = { ...this.user }
    Object.assign(userCopy, newUser);
    this.setState({ user: userCopy })
  }

  setUser(user: IApiLoginResponse["user"] | IUserResponse | null): void {
    let updatedUser = user ? new User(user) : new User({});
    this.setState({ user: updatedUser })
  }

  setAccessToken(accessToken: string | null): void {
    if (!accessToken) {
      this.removeAccessToken();
      this.setState({ isAuthenticated: false })
      return;
    }

    this.storeAccessToken(accessToken);
    this.isAuthenticated = true;

    this.refreshTokenBeforeExpire(accessToken);
  }

  setInitialPage(url: string): void {
    this.initialPage = url;
  }

  refreshTokenBeforeExpire(accessToken: string | undefined): void {
    //console.log('REFRESH BEFORE EXPIRE')
    if (!accessToken) return;
    let parsedToken = this.parseAccessToken(accessToken);
    if (!parsedToken) {
      return;
    }
    let refreshMillis = parsedToken.exp * 1000 - Date.now();

    clearTimeout(this.refreshTokenTimeout);
    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshAccessToken(accessToken);
    }, refreshMillis);
  }

  parseAccessToken(accessToken: string): Jwt | undefined {
    //console.log('PARSE')
    if (typeof window === "undefined") return;
    if (!accessToken) return;
    //console.log('TOKEN : ' + accessToken)
    let base64Url = accessToken.split(".")[1];
    let base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64)) as Jwt;
  }


  getStoredAccessToken(): string | undefined {
    //console.log('GET AT')
    if (typeof window === "undefined") {
      return;
    }

    const value = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    return value ? value : undefined;
  }

  isStoredAccessTokenValid(): boolean {
    const jwt = this.parseAccessToken(this.getStoredAccessToken() || "");
    if (!jwt) {
      return false;
    }

    return jwt.exp * 1000 > new Date().getTime();
  }

  storeAccessToken(accessToken: string): void {
    //console.log('SET AT')
    if (typeof window === "undefined") return;
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  removeAccessToken(): void {
    //console.log('REMOVE AT')
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  redirectHome(): void {
    if(!this.isAuthenticated) return
    if (this.user.schoolContentEnabled) {
      appHistory.push("/myschool");
    } else if (this.user.districtContentEnabled) {
      appHistory.push("/mydistrict")
    } else {
      appHistory.push("/")
    };
  }
}
